// Brand-blue palette subset — for per-user palette assignment.
//
// Every user gets a deterministic brand-blue palette hashed from their
// userId. This subset is intentionally narrow: only blue/cyan/indigo
// family presets, NOT outcome colours and NOT brand-rose / brand-amber /
// brand-emerald variants. Keeps the platform identity blue across all
// user-bearing surfaces (avatars + covers).
//
// V1: the lib only ships the `brand` preset family today, so all users
// hash onto presets that pull from `--brand-{50..950}`. As soon as
// dedicated `--brand-blue-*` / `--brand-sky-*` etc. CSS tokens land in
// globals.css, the corresponding entries will be added to PRESET_TOKENS_RAW
// in palettes.ts and this list expands automatically without callsite
// changes.

import { hashStringToSeed } from '../core/derive';
import { DEFAULT_PALETTE_NAME, type PaletteName } from './palettes';

/**
 * Curated subset of PALETTE_NAMES used for per-user identity colour.
 * All current entries pull from --brand-* tokens (the canonical brand
 * blue family). Each preset has subtly different stop weighting so users
 * still feel distinct.
 */
export const BRAND_BLUE_PALETTES: readonly PaletteName[] = [
  'brand', // balanced
  'deep', // wide range
  'rich', // saturated dark
  'bright', // light bias
  'shadow', // dark dramatic
  'soft', // pale ethereal
  'bold', // saturation peak
  'wide', // extreme spread
  'classic', // balanced 5-stop
  'glow', // bright luminous
];

/**
 * Deterministically pick a brand-blue palette for a given user. Same
 * userId → same palette, every render, every device. Pure function.
 *
 * Falls back to DEFAULT_PALETTE_NAME if the subset is empty (cannot
 * happen by construction; defensive only).
 */
export function pickBrandBluePalette(userId: string | null | undefined): PaletteName {
  if (typeof userId !== 'string' || userId.length === 0) return DEFAULT_PALETTE_NAME;
  if (BRAND_BLUE_PALETTES.length === 0) return DEFAULT_PALETTE_NAME;
  const idx = hashStringToSeed(userId) % BRAND_BLUE_PALETTES.length;
  return BRAND_BLUE_PALETTES[idx] ?? DEFAULT_PALETTE_NAME;
}
